import { motion } from "framer-motion";
import { Play, ShoppingBag, Truck, BellRing, ScanLine, ArrowUpRight } from "lucide-react";
import { BRAND } from "@/lib/data";

const perks = [
  { icon: ShoppingBag, text: "Browse the full shelf & build your cart" },
  { icon: Truck, text: "Doorstep delivery across Dabok" },
  { icon: BellRing, text: "Get alerts on weekly offers & deals" },
];

export const AppDownload = () => {
  return (
    <section
      id="app"
      className="py-24 lg:py-32 bg-foreground text-primary-foreground grain overflow-hidden"
      data-testid="app-section"
    >
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <p className="overline text-accent mb-6">06 / Shop On The Go</p>
          <h2 className="font-display text-4xl sm:text-6xl leading-none tracking-tight">
            Get the <em className="italic text-accent">DV Mart</em> app.
          </h2>
          <p className="mt-6 text-white/60 text-base leading-relaxed max-w-md">
            Order your groceries in a few taps and have them delivered to your
            door — the whole store, right in your pocket.
          </p>

          <ul className="mt-10 space-y-4">
            {perks.map((p) => (
              <li key={p.text} className="flex items-center gap-4">
                <span className="w-10 h-10 shrink-0 border border-white/20 flex items-center justify-center text-accent">
                  <p.icon size={17} />
                </span>
                <span className="text-white/80">{p.text}</span>
              </li>
            ))}
          </ul>

          <p className="mt-10 text-sm text-white/50">
            Available for Android on Google Play.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 40 }}
          whileInView={{ opacity: 1, scale: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <div className="relative mx-auto max-w-md rounded-3xl border border-white/15 bg-white/[0.04] p-6 sm:p-10 text-center shadow-2xl">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-medium tracking-wide text-white/80">
              <ScanLine size={16} className="text-accent" aria-hidden="true" />
              SCAN. DOWNLOAD. SHOP.
            </div>
            <h3 className="font-display text-3xl sm:text-4xl">Your next grocery run,<br />a scan away.</h3>
            <p className="mt-3 text-sm leading-relaxed text-white/60">
              Open your phone’s camera and scan to get the DV Mart app.
            </p>
            <a
              href={BRAND.playStore}
              aria-label="Open DV Mart on Google Play"
              className="mx-auto mt-8 block w-fit rounded-2xl bg-white p-4 shadow-xl transition-transform duration-300 hover:scale-[1.02] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white"
              data-testid="playstore-qr-link"
            >
              <img
                src={`${process.env.PUBLIC_URL}/images/dvmart-playstore-qr.svg`}
                alt="QR code to download DV Mart from Google Play"
                width="216"
                height="216"
                className="h-auto w-[216px] max-w-full"
                data-testid="playstore-qr"
              />
            </a>
            <p className="mt-5 text-xs text-white/50">Already on your phone? Tap below.</p>
            <a
              href={BRAND.playStore}
              className="mt-4 flex w-full items-center justify-center gap-4 rounded-xl bg-accent px-5 py-4 text-white transition-colors duration-300 hover:bg-white hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white group"
              data-testid="playstore-btn"
            >
              <Play size={28} fill="currentColor" strokeWidth={0} aria-hidden="true" />
              <span className="text-left leading-tight">
                <span className="block text-[0.65rem] uppercase tracking-widest opacity-80">Get it on</span>
                <span className="block text-xl font-semibold">Google Play</span>
              </span>
              <ArrowUpRight size={20} className="ml-2" aria-hidden="true" />
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
